import { Autocomplete, Link, Stack, StackProps, TextField, Typography } from '@mui/material';
import { debounce, find, floor, isNil, range, round } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { CustomerInfoResponse, useCustomerInfo } from '@/api/customers';
import { SubscriptionOptionItem, useSubscriptionsInfo, useUpcomingInvoice } from '@/api/subscriptions';
import { usePurchasableItems } from '@/api/subscriptions/getPurchasableItems';
import { queryClient } from '@/common/react-query';
import { DisplayCurrency } from '@/components/DisplayCurrency';
import { Null } from '@/components/Null';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { PaymentConfirmation } from '@/components/transactions/PaymentConfirmation';
import { TransactionIntentFooter } from '@/components/transactions/TransactionIntentFooter';
import {
    TotalDueReadout,
    TransactionDescription,
    TransactionModal,
    TransactionSummary,
} from '@/components/transactions/TransactionModal';
import { TransactionQuantityOptionListItem } from '@/components/transactions/TransactionQuantityOptionListItem';
import { InlineAlert, InlineAlertBody } from '@/components/ui/InlineAlert';
import { ACCOUNT_BALANCE_CREDIT_PRICE_LOOKUP_KEY, STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY } from '@/config';
import { TransactionContextProvider, useTransactionContextLineItemQuantity } from '@/context/transaction';
import { itemQuantity } from '@/utils/languageHelpers';

/**
 * Long enough after a successful transaction for stripe to call our hook and us to grant the purchased credit. We'll force the UI to show
 * the presumed new balance in the meantime, so erring long to make sure to give stripe+tollbooth enough time.
 */
const BALANCE_REFRESH_DELAY = 15_000;

export const AccountBalancePaymentTransaction = () => {
    const { data: purchasableItems } = usePurchasableItems();
    const { data: customerInfo, isPending: customerInfoPending } = useCustomerInfo();
    const { data: subscriptionsInfo, isPending: subsInfoPending } = useSubscriptionsInfo();
    const { data: upcomingInvoice, isPending: upcomingInvoicePending } = useUpcomingInvoice({
        subscriptionId: subscriptionsInfo?.data?.[0]?.id,
    });
    const mixpanel = useMixpanel();

    const isLoading = Boolean(
        customerInfoPending || subsInfoPending || (subscriptionsInfo?.data?.[0]?.id && upcomingInvoicePending)
    );

    const customerBalance = (customerInfo?.data.balance ?? 0) / 100;
    const nextInvoiceTotal = (upcomingInvoice?.data?.total ?? 0) / 100;

    const quantityOptions = useMemo(() => {
        return getAccountBalanceFundingOptions({
            customerBalance,
            nextInvoiceTotal,
        });
    }, [customerBalance, nextInvoiceTotal]);

    const [confirming, setConfirming] = useState(false);
    const item = useMemo(
        () => find(purchasableItems, { price_lookup_key: ACCOUNT_BALANCE_CREDIT_PRICE_LOOKUP_KEY }),
        [purchasableItems]
    );
    const [currentQuantity, setCurrentQuantity] = useState<number>();

    function handleConfirm() {
        const newBalance = -customerBalance + (currentQuantity ?? 0);
        mixpanel?.track('confirm-account-balance-payment', {
            nextInvoiceTotal,
            oldBalance: -customerBalance,
            newBalance,
            oldMonthsCovered: Math.floor(-customerBalance / nextInvoiceTotal),
            newMonthsCovered: Math.floor(newBalance / nextInvoiceTotal),
        });
        setConfirming(true);
    }

    const handleSuccess = useCallback(() => {
        // there is some delay before Stripe calls us back with the completed transaction and we grant the purchased balance, so
        // after a successful transaction, stick the new balance into the cache directly, and queue up a cache invalidated to fetch
        // the server's version after a little delay
        queryClient.setQueryData([STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY], (customerInfo?: CustomerInfoResponse) => {
            if (!currentQuantity) {
                return customerInfo;
            }
            const { data, ...moreCustomerInfo } = customerInfo ?? { status: 'success' };
            const { balance, ...moreData } = data ?? { balance: 0 };
            return { data: { balance: balance - currentQuantity * 100, ...moreData }, ...moreCustomerInfo };
        });
        setTimeout(
            () => queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY] }),
            BALANCE_REFRESH_DELAY
        );
    }, [currentQuantity]);

    return (
        <TransactionContextProvider type="payment" defaultBackTarget="payment" notFromAccountBalance>
            <TransactionModal>
                <TransactionDescription title="Add account balance">
                    Automatic monthly invoices will draw from your account balance first. Any remaining amount not
                    covered will then be charged to your payment method. Estimates are based on your current number of
                    enabled devices and data subscription. Learn more about Spotter data subscriptions and calculate
                    your monthly usage with our{' '}
                    <Link
                        href="https://sofarocean.notion.site/Spotter-Data-Subscription-Renewals-and-Pricing-58a4fa5351f44bd48e8b41bbda41ac5f"
                        target="_blank"
                    >
                        support documentation
                    </Link>
                    .
                </TransactionDescription>
                <TransactionSummary>
                    {isLoading ? (
                        <GenericSkeleton lines={1} />
                    ) : (
                        <>
                            <AccountCreditAmountEditor
                                item={item}
                                onChange={setCurrentQuantity}
                                quantityOptions={quantityOptions}
                            />
                            <TotalDueReadout />
                        </>
                    )}
                </TransactionSummary>
                <AccountBalanceTransactionSummary amountToAdd={currentQuantity} />
                <TransactionIntentFooter onConfirm={handleConfirm} />
            </TransactionModal>
            <PaymentConfirmation
                open={confirming}
                onClose={() => setConfirming(false)}
                onSuccess={handleSuccess}
                transactionTitleName="Account balance addition"
                successMessage="Funds in your account balance are ready for immediate use."
                paymentProcessingMessage="Your payment is still processing. Funds will be added to your account balance once the payment has completed, which may take a few days."
            ></PaymentConfirmation>
        </TransactionContextProvider>
    );
};

/**
 * @param customerBalance negative if they have credit!
 */
function getAccountBalanceFundingOptions({
    customerBalance = 0,
    nextInvoiceTotal,
}: {
    customerBalance?: number;
    nextInvoiceTotal?: number;
}) {
    if (!nextInvoiceTotal) {
        return range(50, 301, 50).map((value) => ({ value, plusMonths: null }));
    }

    const currentCredit = -customerBalance;
    const balanceDue = customerBalance > 0;

    // baseValue is the amount of balance needed to get us up to the next round number of invoices
    const baseValue = balanceDue
        ? customerBalance + nextInvoiceTotal
        : nextInvoiceTotal - (currentCredit % nextInvoiceTotal);
    // baseTotalMonths is the number of months that will be covered after baseValue is applied
    const baseTotalMonths = balanceDue ? 0 : floor(currentCredit / nextInvoiceTotal);

    // first three options are +1, +2, and +3 months, plus an extra month if getting us up to the next month is less than a dollar
    const plusExtraMonth = baseValue < 1 && baseValue > 0 ? 1 : 0;
    const plusMonthOptions = range(1 + plusExtraMonth, 4 + plusExtraMonth);

    // next two options are cover up to 6 and 12 total months, unless that's already covered by the +1-3 options above, in which
    // case we add in +12 and +6 entries to get up to 5 options.
    if (baseTotalMonths + plusExtraMonth < 3) {
        plusMonthOptions.push(6 - baseTotalMonths);
    } else {
        plusMonthOptions.push(12);
    }
    if (baseTotalMonths + plusExtraMonth < 9) {
        plusMonthOptions.push(12 - baseTotalMonths);
    } else {
        plusMonthOptions.push(6);
    }

    plusMonthOptions.sort((a, b) => a - b);
    return plusMonthOptions.map((n) => ({
        value: round(baseValue + (n - 1) * nextInvoiceTotal, 2),
        plusMonths: n,
    }));
}

export type AccountCreditAmountEditorProps = {
    item?: SubscriptionOptionItem;
    onChange?: (quantity: number) => void;
    quantityOptions: { value: number; plusMonths: number | null }[];
} & Pick<StackProps, 'sx'>;

export const AccountCreditAmountEditor = ({ onChange, item, quantityOptions, sx }: AccountCreditAmountEditorProps) => {
    const [open, setOpen] = useState(false);
    const [quantityString, setQuantityString] = useState<string>();

    const [initialQuantity, setItemQuantity] = useTransactionContextLineItemQuantity(
        item?.price_id,
        quantityOptions[0].value
    );

    useEffect(() => {
        if (isNil(quantityString)) {
            setItemQuantity(initialQuantity);
            setQuantityString(initialQuantity.toFixed(2));
            onChange?.(initialQuantity);
        }
    }, [initialQuantity, onChange, quantityOptions, quantityString, setItemQuantity]);

    const debouncedSetItemQuantity = useMemo(
        () =>
            debounce((value) => setItemQuantity(value), 150, {
                trailing: true,
            }),

        [setItemQuantity]
    );

    const handleChange = useCallback(
        (_event: unknown, valStr: string) => {
            // split across first dot, replace everything that isn't a digit, then add the one dot back in
            const qtyString = valStr
                .split(/\./, 2)
                .map((part) => part.replaceAll(/[^\d]/g, ''))
                .join('.');
            const value = Number(qtyString);
            setQuantityString(qtyString);
            if (value >= 1) {
                debouncedSetItemQuantity(value);
                onChange?.(value);
            }
        },
        [debouncedSetItemQuantity, onChange]
    );

    if (!item) {
        return <GenericSkeleton lines={1} />;
    }

    if (item.currency !== 'usd') {
        // This component assumes the item costs $1 and quantity is exactly the number of dollars you want to add to your balance.
        return (
            <InlineAlert severity="error">
                <InlineAlertBody>This page only supports adding balance in USD</InlineAlertBody>
            </InlineAlert>
        );
    }

    const { currency } = item;

    return (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', ...sx }}>
            <Typography variant="h5">Amount to add:</Typography>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                <Typography>$</Typography>
                <Autocomplete
                    sx={{ width: '10em' }}
                    size="small"
                    value={quantityString ?? ''}
                    options={quantityOptions.map(({ value }) => value.toFixed(2))}
                    filterOptions={(options) => options}
                    includeInputInList
                    freeSolo
                    selectOnFocus
                    forcePopupIcon
                    disableClearable
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    renderOption={({ key, ...props }, optionStr) => {
                        const option = quantityOptions.find(({ value }) => value === Number(optionStr));
                        return (
                            <TransactionQuantityOptionListItem
                                key={key}
                                {...props}
                                label={optionStr}
                                description={option?.plusMonths ? `+${itemQuantity(option.plusMonths, 'month')}` : null}
                            />
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            data-testid="account-credit-amount-input"
                            onBlur={() => setQuantityString(Math.max(1, Number(quantityString)).toFixed(2))}
                            onKeyDown={(event) => {
                                if (
                                    event.key.length === 1 &&
                                    !(event.ctrlKey || event.altKey || event.metaKey) &&
                                    // allow digits or dot if there isn't already a dot
                                    !(event.key.match(/\d/) || (event.key === '.' && !quantityString?.match(/\./)))
                                ) {
                                    event.preventDefault();
                                    return;
                                }
                                if (event.key === 'Enter') {
                                    setOpen(false);
                                }
                            }}
                        />
                    )}
                    onChange={handleChange}
                    onInputChange={handleChange}
                />
                <Typography>{currency.toUpperCase()}</Typography>
            </Stack>
        </Stack>
    );
};

export const AccountBalanceTransactionSummary = ({ amountToAdd }: { amountToAdd?: number }) => {
    const { data: customerInfo } = useCustomerInfo();
    const { data: subscriptionsInfo } = useSubscriptionsInfo();
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionsInfo?.data?.[0]?.id,
    });

    if (isNil(amountToAdd) || isNil(customerInfo?.data.balance)) {
        return false;
    }

    const { currency, balance } = customerInfo!.data;
    const newBalance = balance - amountToAdd * 100;
    const numInvoicesCovered = Math.floor(-newBalance / (upcomingInvoice?.data.total ?? 0));

    return (
        <TransactionSummary>
            {upcomingInvoice?.data ? (
                <Stack sx={{ gap: 1 }}>
                    <Stack
                        direction="row"
                        data-testid="next-recurring-total-readout"
                        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                    >
                        <Typography variant="h5">Next recurring invoice total:</Typography>
                        <DisplayCurrency
                            variant="body1"
                            value={upcomingInvoice.data.total}
                            stickyValueOnLoading
                            cents
                            currency={upcomingInvoice.data.currency}
                        />
                    </Stack>
                </Stack>
            ) : (
                <Null data-testid="no-next-recurring-total" />
            )}
            <Stack sx={{ gap: 3 }}>
                <Stack
                    direction="row"
                    data-testid="current-account-balance-readout"
                    sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                >
                    <Typography variant="h5">Current account balance:</Typography>
                    <DisplayCurrency variant="body1" value={balance} negative cents currency={currency} />
                </Stack>
                <Stack
                    direction="row"
                    data-testid="new-account-balance-readout"
                    sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                >
                    <Typography variant="h5">New account balance:</Typography>
                    <DisplayCurrency
                        variant="body1"
                        value={newBalance}
                        negative
                        stickyValueOnLoading
                        cents
                        currency={currency}
                    />
                </Stack>
                {upcomingInvoice?.data ? (
                    <InlineAlert severity="tip" icon={false} data-testid="num-invoices-covered-alert">
                        {numInvoicesCovered > 0 ? (
                            <InlineAlertBody>
                                Your new account balance is estimated to fully pay your{' '}
                                <strong>
                                    next {itemQuantity(numInvoicesCovered, 'recurring invoice', { omitOne: true })}
                                </strong>
                                . Estimates are based on your current number of enabled devices and data subscription.
                            </InlineAlertBody>
                        ) : (
                            <InlineAlertBody>
                                Your new account balance will be{' '}
                                <strong>insufficient to cover your next recurring invoice</strong>. Estimates are based
                                on your current number of enabled devices and data subscription.
                            </InlineAlertBody>
                        )}
                    </InlineAlert>
                ) : (
                    <Null data-testid="no-num-invoices-covered-alert" />
                )}
            </Stack>
        </TransactionSummary>
    );
};

export const exportForTests = { getAccountBalanceFundingOptions };
